/*
  Shop by, then refine — instant-apply collection filtering.

  Every checkbox tick, chip removal, price change and sort change re-fetches
  this section's HTML (?section_id=...) with the new params and swaps the
  product grid, chips, counts and drawer groups in place. There is no Apply
  step; the drawer can stay open while results change behind it.
*/

class FsCollectionFilters extends HTMLElement {
  connectedCallback() {
    if (this.dataset.fsInit) return;
    this.dataset.fsInit = '1';

    this.sectionId = this.dataset.sectionId;
    this.openGroups = new Set(
      Array.from(this.querySelectorAll('.fs-fd-group[data-open]')).map((group) => group.dataset.fsGroup)
    );
    this.abortController = null;
    this.debounceTimer = null;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
    });

    /* The empty state's "Remove all filters" link lives inside the product
       grid container, outside this element, and is re-created on every
       AJAX swap — so delegate from the document. */
    document.addEventListener('click', (event) => {
      const clear = event.target.closest('[data-fs-clear-all]');
      if (clear && !this.contains(clear)) {
        event.preventDefault();
        this.clearAll();
      }
    });

    window.addEventListener('popstate', () => {
      this.renderParams(window.location.search.slice(1), false);
    });
  }

  onClick(event) {
    if (event.target.closest('[data-fs-open]')) return this.openDrawer();
    if (event.target.closest('[data-fs-close]')) return this.closeDrawer();

    const head = event.target.closest('.fs-fd-group__head');
    if (head) return this.toggleGroup(head.closest('.fs-fd-group'));

    const clear = event.target.closest('[data-fs-clear-all]');
    if (clear) {
      event.preventDefault();
      return this.clearAll();
    }

    const chip = event.target.closest('[data-fs-chip]');
    if (chip) {
      event.preventDefault();
      return this.renderParams(this.paramsFromUrl(chip.href));
    }
  }

  onChange(event) {
    if (event.target.matches('[data-fs-sort]')) {
      this.querySelectorAll('[data-fs-sort]').forEach((select) => {
        select.value = event.target.value;
      });
      return this.apply();
    }
    if (event.target.closest('#fs-drawer-groups')) this.apply();
  }

  apply() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.renderParams(this.buildParams()), 150);
  }

  buildParams() {
    const params = new URLSearchParams();
    this.querySelectorAll('#fs-drawer-groups input[type="checkbox"]:checked').forEach((input) => {
      params.append(input.name, input.value);
    });
    this.querySelectorAll('#fs-drawer-groups input[data-fs-price]').forEach((input) => {
      if (input.value !== '') params.append(input.name, input.value);
    });
    this.appendSort(params);
    return params.toString();
  }

  /* Chip hrefs come from Shopify's url_to_remove, which keeps the other
     filter params. Sort is state of its own, so carry it across if the
     target URL dropped it. */
  paramsFromUrl(href) {
    const params = new URL(href, window.location.origin).searchParams;
    if (!params.has('sort_by')) this.appendSort(params);
    return params.toString();
  }

  appendSort(params) {
    const sort = this.querySelector('[data-fs-sort]');
    /* "Featured" (manual) is the collection's default order — cleanest as no param at all. */
    if (sort && sort.value && sort.value !== 'manual') params.set('sort_by', sort.value);
  }

  clearAll() {
    const params = new URLSearchParams();
    this.appendSort(params);
    this.renderParams(params.toString());
  }

  renderParams(params, pushHistory = true) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const grid = document.getElementById('ProductGridContainer');
    if (grid) grid.classList.add('fs-loading');

    const pageUrl = `${window.location.pathname}${params ? '?' + params : ''}`;
    const fetchUrl = `${window.location.pathname}?section_id=${this.sectionId}${params ? '&' + params : ''}`;

    fetch(fetchUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.renderResponse(new DOMParser().parseFromString(responseText, 'text/html'));
        if (pushHistory) history.pushState({}, '', pageUrl);
      })
      .catch((error) => {
        /* An aborted fetch means a newer request took over — do nothing.
           A real failure falls back to full navigation so filtering still works. */
        if (error.name !== 'AbortError') window.location.href = pageUrl;
      });
  }

  renderResponse(doc) {
    const grid = document.getElementById('ProductGridContainer');
    const newGrid = doc.getElementById('ProductGridContainer');
    if (grid && newGrid) {
      grid.innerHTML = newGrid.innerHTML;
      grid.classList.remove('fs-loading');
    }

    /* The masonry grid-template-areas style is generated from the product
       count, outside the grid container — swap it too or the layout breaks
       when the count grows (e.g. removing filters after a filtered landing). */
    const styleNeedle = `product-grid--masonry-${this.sectionId}`;
    const newStyle = Array.from(doc.querySelectorAll('style')).find((s) => s.textContent.includes(styleNeedle));
    const curStyle = Array.from(document.querySelectorAll('style')).find((s) => s.textContent.includes(styleNeedle));
    if (newStyle && curStyle) curStyle.textContent = newStyle.textContent;

    ['fs-chips-inline', 'fs-chips-scroll', 'fs-filters-label', 'fs-count-line', 'fs-show-btn'].forEach((id) => {
      const current = document.getElementById(id);
      const next = doc.getElementById(id);
      if (current && next) current.innerHTML = next.innerHTML;
    });

    const drawerBody = this.querySelector('.fs-cf__drawer-body');
    const scrollTop = drawerBody ? drawerBody.scrollTop : 0;
    const activeId = document.activeElement && document.activeElement.id;

    const currentGroups = document.getElementById('fs-drawer-groups');
    const nextGroups = doc.getElementById('fs-drawer-groups');
    if (currentGroups && nextGroups) {
      currentGroups.innerHTML = nextGroups.innerHTML;
      currentGroups.querySelectorAll('.fs-fd-group').forEach((group) => {
        const open = this.openGroups.has(group.dataset.fsGroup);
        group.toggleAttribute('data-open', open);
        const head = group.querySelector('.fs-fd-group__head');
        if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    if (drawerBody) drawerBody.scrollTop = scrollTop;
    if (activeId) {
      const toFocus = document.getElementById(activeId);
      if (toFocus) toFocus.focus({ preventScroll: true });
    }

    const nextSort = doc.querySelector('[data-fs-sort]');
    if (nextSort) {
      this.querySelectorAll('[data-fs-sort]').forEach((select) => {
        select.value = nextSort.value;
      });
    }
  }

  toggleGroup(group) {
    if (!group) return;
    const open = !group.hasAttribute('data-open');
    group.toggleAttribute('data-open', open);
    const head = group.querySelector('.fs-fd-group__head');
    if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.openGroups.add(group.dataset.fsGroup);
    else this.openGroups.delete(group.dataset.fsGroup);
  }

  openDrawer() {
    document.body.classList.add('fs-filters-open');
    const close = this.querySelector('.fs-cf__drawer-close');
    if (close) close.focus();
  }

  closeDrawer() {
    if (!document.body.classList.contains('fs-filters-open')) return;
    document.body.classList.remove('fs-filters-open');
    const trigger = this.querySelector('[data-fs-open]');
    if (trigger) trigger.focus();
  }
}

if (!customElements.get('fs-collection-filters')) {
  customElements.define('fs-collection-filters', FsCollectionFilters);
}
